import React, {useEffect, useState, useCallback} from 'react';
import { BodyWrapper } from '../components/BodyWrapper';
import { PasswordBox } from '../components/PasswordBox';
import { QrCodeButton } from '../components/QrCodeButton';
import { PromptField } from '../components/PromptField';
import { TerminalButton, TerminalGhostButton } from '../components/TerminalButton';
import { TerminalFrame } from '../components/TerminalFrame';
import { setTitle } from '../utils/title';
import { pingPageView } from '../utils/analytics';
import { FileAttach } from '../components/FileAttach';
import { getPad, addMessage, uploadPadFiles } from '../utils/fetch';
import { Colors, Fonts } from '../utils/colors';
import { copyToClipboard, formatFileSize, getPadLink } from '../utils/util';
import Snackbar from '@material-ui/core/Snackbar';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Share from '@material-ui/icons/Share';
import styled from 'styled-components';
import { GetPadResponse, Pad, Memo, MemoType } from '../models/Pad';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import moment from 'moment';
import Linkify from 'react-linkify';

interface PadPageParams {
  name: string
}

interface PadLocationState {
  password?: string
}

export const PadPage = (props: RouteComponentProps<PadPageParams, {}, PadLocationState>) => {
  const [pad, setPad] = useState<Pad | null>(null)
  const [truePassword, setTruePassword] = useState("")
  const [padInput, setPadInput] = useState("")
  const [isNonExistent, setIsNonExistent] = useState(false)
  const [errorStr, setErrorStr] = useState<string | null>(null)
  const [appendErrorStr, setAppendErrorStr] = useState<string | null>(null)
  const [isPasswordRequired, setIsPasswordRequired] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [isAppendInProgress, setIsAppendInProgress] = useState(false)
  const [isLinkCopied, setIsLinkCopied] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [uploadLabel, setUploadLabel] = useState<string | null>(null)

  const padName = props.match.params.name
  const padLink = getPadLink(padName)
  const createdPassword =
    (props.location.state && props.location.state.password) || ""

  const redirectName = (): string | null => {
    const lowerName = padName.toLowerCase()
    if (padName !== lowerName) {
      return lowerName
    } else {
      return null
    }
  }

  const copy = () => {
    copyToClipboard(padLink)
    setIsLinkCopied(true)
    window.setTimeout(() => setIsLinkCopied(false), 1500)
  }

  const applyPadResponse = useCallback((
    padResponse: GetPadResponse,
    password: string,
  ): boolean => {
    if (padResponse.error_code) {
      switch(padResponse.error_code) {
        case 1:
          setIsNonExistent(true)
          break;
        case 2:
          setIsPasswordRequired(true)
          if (password !== "") {
            setPasswordError("Wrong password")
          }
          break;
        case 3:
          setAppendErrorStr(padResponse.error_message)
          break;
        default:
          throw Error(padResponse.error_message)
      }
      return false
    }
    setTruePassword(password)
    setPasswordError(null)
    setIsPasswordRequired(false)
    setPad(padResponse)
    return true
  }, [])

  const fillPad = useCallback(async (responseFn: () => Promise<GetPadResponse>, password: string) => {
    try {
      applyPadResponse(await responseFn(), password)
    } catch (error) {
      setErrorStr((error as Error).message)
    }
  }, [applyPadResponse])

  const loadPad = useCallback(async (password: string) => {
    const doGetPad = async (): Promise<GetPadResponse> => {
      return await getPad(padName, password)
    }
    fillPad(doGetPad, password)
  }, [padName, fillPad])

  useEffect(() => {
    setTitle(padName)
    pingPageView()
    loadPad(createdPassword)
  }, [padName, loadPad, createdPassword])

  useEffect(() => {
    const interval = setInterval(() => loadPad(truePassword), 30000);
    return () => {
      clearInterval(interval);
    };
  }, [loadPad, truePassword])

  const handleAppend = async () => {
    if (isAppendInProgress || (padInput === "" && selectedFiles.length === 0)) {
      return
    }
    setIsAppendInProgress(true)
    setAppendErrorStr(null)
    setUploadProgress(null)
    setUploadLabel(null)
    try {
      if (selectedFiles.length > 0) {
        const padResponse: GetPadResponse =
          await uploadPadFiles(
            padName,
            selectedFiles,
            truePassword,
            (update) => {
              setUploadProgress(update.percent)
              setUploadLabel(
                `uploading ${update.current}/${update.total}: ${update.fileName}`
              )
            },
          )
        if (!applyPadResponse(padResponse, truePassword)) {
          return
        }
        setSelectedFiles([])
      }
      if (padInput !== "") {
        const padResponse: GetPadResponse =
          await addMessage(padName, padInput, truePassword)
        if (!applyPadResponse(padResponse, truePassword)) {
          return
        }
        setPadInput("")
      }
    } catch (error) {
      setAppendErrorStr((error as Error).message)
    } finally {
      setIsAppendInProgress(false)
      setUploadProgress(null)
      setUploadLabel(null)
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPadInput(event.target.value)
  }

  const handlePasswordSubmit = (password: string) => {
    setPasswordError(null)
    loadPad(password)
  }

  if (redirectName()) {
    return (
      <Redirect
        to={{
          pathname: `/${redirectName()}`,
          state: props.location.state,
        }}
      />
    )
  }

  var padContent = <LoadingLine>loading pad...</LoadingLine>
  if (isNonExistent) {
    padContent = <StatusLine>pad has been burned</StatusLine>
  } else if (errorStr) {
    padContent = <StatusLine>{errorStr}</StatusLine>
  } else if (pad) {
    padContent = (
      <PadLayout>
        <PadBox pad={pad} />
        <Composer>
          <PromptField
            value={padInput}
            onChange={handleInputChange}
            multiline
            rows={3}
            placeholder="write a note"
          />
          <Snackbar
            open={appendErrorStr != null}
            autoHideDuration={5000}
            message={appendErrorStr}
            onClose={() => setAppendErrorStr(null)}
          />
          <ComposerActions>
            <FileAttach
              files={selectedFiles}
              onChange={setSelectedFiles}
              disabled={isAppendInProgress}
              uploadProgress={uploadProgress}
              uploadLabel={uploadLabel}
            />
            <TerminalButton
              type="button"
              disabled={isAppendInProgress || (padInput === "" && selectedFiles.length === 0)}
              onClick={handleAppend}
            >
              write
            </TerminalButton>
          </ComposerActions>
        </Composer>
        <ExpirationTicker expirationDate={moment(pad.expiration_time)} />
      </PadLayout>
    )
  } else if (isPasswordRequired) {
    padContent = (
      <PasswordBox
        error={passwordError}
        onSubmit={handlePasswordSubmit}
      />
    )
  }

  return (
    <BodyWrapper>
      <Page>
        <TerminalFrame title={padName}>
          <ActionBar>
            <QrCodeButton padLink={padLink} />
            <TerminalGhostButton type="button" onClick={copy}>
              <Share fontSize="small" />
              {isLinkCopied ? "copied" : "share"}
            </TerminalGhostButton>
            {truePassword !== "" && <PasswordReveal password={truePassword} />}
          </ActionBar>
          {padContent}
        </TerminalFrame>
      </Page>
    </BodyWrapper>
  )
}

const PasswordReveal = (props: {password: string}) => {
  const [isVisible, setIsVisible] = useState(false)
  const display = isVisible
    ? props.password
    : "•".repeat(Math.max(props.password.length, 1))

  return (
    <PasswordRevealWrap>
      <PasswordLabel>password</PasswordLabel>
      <PasswordSlot>
        <PasswordSizer aria-hidden="true">{props.password}</PasswordSizer>
        <PasswordText>{display}</PasswordText>
      </PasswordSlot>
      <IconHit
        type="button"
        aria-label={isVisible ? "Hide password" : "Show password"}
        onClick={() => setIsVisible(!isVisible)}
      >
        {isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconHit>
    </PasswordRevealWrap>
  )
}

const getMemoMeta = (memo: Memo) => {
  const when = moment(memo.timestamp).format("M/D/YY, h:mm:ssa")
  return memo.location ? `${when}  ·  ${memo.location}` : when
}

export const PadBox = (props: {pad: Pad}) => {
  return (
    <MemoList>
      {props.pad.memos.map((memo, i) => (
        <MemoBlock key={i}>
          <MemoBox memo={memo} />
          <MemoFooter>
            <MemoMeta>{getMemoMeta(memo)}</MemoMeta>
            {memo.memo_type === MemoType.Message && memo.message != null && (
              <CopyMemoButton text={memo.message} />
            )}
          </MemoFooter>
        </MemoBlock>
      ))}
    </MemoList>
  )
}

const CopyMemoButton = (props: {text: string}) => {
  const [copied, setCopied] = useState(false)

  const copy = () => {
    copyToClipboard(props.text)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <CopyButton
      type="button"
      $copied={copied}
      onClick={copy}
      aria-label={copied ? "Copied" : "Copy note"}
    >
      {copied ? "copied" : "copy"}
    </CopyButton>
  )
}

export const MemoBox = (props: {memo: Memo}) => {
  if (props.memo.memo_type === MemoType.Message) {
    return <MessageBox memo={props.memo} />
  } else {
    return <FileBox memo={props.memo} />
  }
}

export const MessageBox = (props: {memo: Memo}) => {
  return (
    <MemoRow>
      <Caret>&gt;</Caret>
      <Linkify>
        <MessageText>{props.memo.message}</MessageText>
      </Linkify>
    </MemoRow>
  )
}

export const FileBox = (props: {memo: Memo}) => {
  const file = props.memo.file
  if (!file) {
    return null
  }
  return (
    <MemoRow>
      <Caret>&gt;</Caret>
      <FileLink href={file.link}>
        {file.name}
        <FileSize> {formatFileSize(file.size)}</FileSize>
      </FileLink>
    </MemoRow>
  )
}

export const ExpirationTicker = (props: {expirationDate: moment.Moment}) => {
  const [now, setNow] = useState(moment.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(moment.now()), 500);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const diff = moment.duration(props.expirationDate.diff(now))
  if (diff.asSeconds() <= 0) {
    return <Redirect to='/' />
  }

  const countdownStr =
    `${zeroPad(Math.floor(diff.asHours()))}:` +
      `${zeroPad(diff.minutes())}:` +
      `${zeroPad(diff.seconds())}`
  return (
    <StatusLine>[ self-destructing in {countdownStr} ]</StatusLine>
  )
}

const zeroPad = (num: number): string => {
  return String(num).padStart(2, "0")
}

const Page = styled.div`
  max-width: 720px;
  margin: 0 auto 32px;
`;

const PadLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ComposerActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;

  ${TerminalButton} {
    align-self: flex-start;
  }
`;

const FileLink = styled.a`
  color: ${Colors.destructGreen};
  font-size: 14px;
  line-height: 1.55;
  word-break: break-all;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const FileSize = styled.span`
  color: ${Colors.terminalMuted};
  font-size: 12px;
  white-space: nowrap;
`;

const MemoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const MemoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const Caret = styled.span`
  flex-shrink: 0;
  color: ${Colors.destructGreen};
  text-shadow: 0 0 8px ${Colors.terminalGlow};
  user-select: none;
`;

const MessageText = styled.span`
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.55;
  color: ${Colors.terminalText};

  a {
    color: ${Colors.destructGreen};
  }
`;

const MemoFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-left: 22px;
`;

const MemoMeta = styled.div`
  font-size: 11px;
  letter-spacing: 0.02em;
  color: ${Colors.terminalDim};
  opacity: 0.72;
  transition: color 0.16s ease, opacity 0.16s ease;

  ${MemoBlock}:hover &,
  ${MemoBlock}:focus-within & {
    opacity: 1;
    color: ${Colors.terminalMuted};
  }
`;

const CopyButton = styled.button<{$copied?: boolean}>`
  display: none;
  appearance: none;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => props.$copied ? Colors.destructGreen : Colors.terminalBorder};
  background: transparent;
  margin: 0;
  padding: 8px 12px;
  min-width: 56px;
  min-height: 44px;
  color: ${props => props.$copied ? Colors.destructGreen : Colors.terminalText};
  font-family: ${Fonts.mono};
  font-size: 12px;
  letter-spacing: 0.08em;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  @media (hover: none) and (pointer: coarse) {
    display: inline-flex;
  }

  &:hover,
  &:focus-visible {
    color: ${Colors.destructGreen};
    border-color: ${Colors.destructGreen};
    background: ${Colors.accentSoft};
    outline: none;
  }
`;

const StatusLine = styled.p`
  width: 100%;
  margin: 8px 0 0;
  text-align: center;
  color: ${Colors.terminalMuted};
  font-family: ${Fonts.mono};
  font-size: 13px;
`;

const LoadingLine = styled(StatusLine)`
  color: ${Colors.destructGreen};
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin: 0 0 18px;
`;

const PasswordRevealWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
`;

const PasswordLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${Colors.terminalMuted};
  white-space: nowrap;
`;

const PasswordSlot = styled.span`
  position: relative;
  display: inline-block;
  font-family: ${Fonts.mono};
  font-size: 13px;
  letter-spacing: 0.04em;
  color: ${Colors.destructGreen};
`;

const PasswordSizer = styled.span`
  visibility: hidden;
  white-space: nowrap;
`;

const PasswordText = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  white-space: nowrap;
`;

const IconHit = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: ${Colors.terminalMuted};
  padding: 4px;
  display: inline-flex;
  cursor: pointer;

  &:hover {
    color: ${Colors.destructGreen};
  }
`;
